import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, map } from 'rxjs';
import { DialogService, FlyoutService } from '@app/core/services';

export interface ComponentCanDeactivate {
	canDeactivate: () => boolean;
}

/**
 * Unsaved changes for NGX -> NGX/AJS
 */
@Injectable()
export class UnsavedChangesGuard {
	constructor(private readonly dialogService: DialogService, private readonly flyoutService: FlyoutService, private readonly router: Router) {}

	canDeactivate(
		component: ComponentCanDeactivate,
		currentRoute: ActivatedRouteSnapshot,
		currentState: RouterStateSnapshot,
		nextState?: RouterStateSnapshot
	): boolean | Observable<boolean | RedirectCommand> {
		const flyout = this.flyoutService.flyout$.value;
		if (flyout) {
			return this.dialogService.canDeactivate(flyout.context, {
				onCanDeactivate: () => {
					this.flyoutService.close();
					this.router.navigateByUrl(nextState.url);
				},
			});
		}

		if (component.canDeactivate) {
			// A guard rejection makes the router put the browser URL back with `replaceState`, which the AngularJS location
			// sync (in the `bootstrap` method of ajs.service.ts) treats as a URL change and re-navigates to (with any trailing
			// slash stripped, e.g. an empty id), re-running this guard in a loop. Redirecting to the current URL instead lets
			// the router restore the URL as part of a normal navigation, which the sync ignores. For browser back/forward the
			// router would replace the history entry the browser moved to, losing one entry per cancel, so `replaceUrl: false`
			// pushes a new entry instead.
			// Once migration to Angular is complete and that bootstrapping sync is removed, we should be able to remove the pipe
			// call, returning to the original behavior.
			return this.dialogService
				.canDeactivate(component)
				.pipe(map((canLeave) => canLeave || new RedirectCommand(this.router.parseUrl(currentState.url), { replaceUrl: false })));
		}

		return true;
	}
}
